'use client';
import { motion } from 'framer-motion';
import { useEffect, useRef, useCallback, useMemo } from 'react';

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
  baseX: number;
  baseY: number;
  pulsePhase: number;
}

interface Dot {
  id: number;
  delay: number;
  duration: number;
  size: number;
  x: string;
  y: string;
}

export const NetworkConstellation = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const nodesRef = useRef<Node[]>([]);
  const mousePositionRef = useRef({ x: 0, y: 0, active: false });
  const dimensionsRef = useRef({ width: 0, height: 0 });

  // Configuration - memoized to prevent unnecessary re-renders
  const config = useMemo(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    return {
      nodeCount: isMobile ? 18 : 60,
      maxDistance: isMobile ? 90 : 150,
      repulsionDistance: 80,
      baseHue: 220,
      mouseForce: 0.15,
      centerForce: 0.0005,
    };
  }, []);

  // Handle mouse movement for interaction
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    mousePositionRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      active: true,
    };
  }, []);

  const handleMouseLeave = useCallback(() => {
    mousePositionRef.current.active = false;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size with high DPI support
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = window.innerWidth;
      const height = window.innerHeight;

      dimensionsRef.current = { width, height };
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      // Reinitialize nodes if canvas size changes significantly
      if (Math.abs(width - (parseInt(canvas.dataset.lastWidth || '0'))) > 100 ||
        Math.abs(height - (parseInt(canvas.dataset.lastHeight || '0'))) > 100) {
        initNodes(width, height);
        canvas.dataset.lastWidth = width.toString();
        canvas.dataset.lastHeight = height.toString();
      }
    };

    // Initialize nodes with more organic distribution
    const initNodes = (width: number, height: number) => {
      const nodes: Node[] = [];
      const centerX = width / 2;
      const centerY = height / 2;

      for (let i = 0; i < config.nodeCount; i++) {
        // Create clusters of nodes for a more constellation-like appearance
        const cluster = i % 4;
        const angle = (i / config.nodeCount) * Math.PI * 2;
        const radius = 80 + (cluster * 120) + (Math.sin(i) * 40);

        const baseX = centerX + Math.cos(angle) * radius;
        const baseY = centerY + Math.sin(angle) * radius;

        // Add some variation to node positions
        const variation = 30;
        const x = baseX + (Math.sin(i * 0.7) * variation);
        const y = baseY + (Math.cos(i * 0.5) * variation);

        nodes.push({
          x,
          y,
          vx: (Math.sin(i) * 0.3) + (Math.random() - 0.5) * 0.4,
          vy: (Math.cos(i) * 0.3) + (Math.random() - 0.5) * 0.4,
          radius: 1.2 + Math.sin(i) * 0.8,
          opacity: 0.4 + Math.cos(i) * 0.15,
          baseX,
          baseY,
          pulsePhase: Math.random() * Math.PI * 2,
        });
      }

      nodesRef.current = nodes;
    };

    resizeCanvas();
    initNodes(canvas.width, canvas.height);

    // Add event listeners
    window.addEventListener('resize', resizeCanvas);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    let isVisible = true;
    let lastFrameTime = performance.now();
    const frameInterval = 1000 / 30; // Throttle to ~30 FPS

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isVisible = entry.isIntersecting;
          if (isVisible) {
            lastFrameTime = performance.now();
            animationRef.current = requestAnimationFrame(animate);
          } else {
            cancelAnimationFrame(animationRef.current);
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(canvas);

    const animate = (time?: number) => {
      if (!ctx || !isVisible) return;

      const currentTime = time || performance.now();
      const elapsed = currentTime - lastFrameTime;

      if (elapsed >= frameInterval) {
        lastFrameTime = currentTime - (elapsed % frameInterval);

        const { width, height } = dimensionsRef.current;

        // Clear canvas with a subtle fade effect for trails - black background
        ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
        ctx.fillRect(0, 0, width, height);

        const nodes = nodesRef.current;
        const mouse = mousePositionRef.current;

        // Update node positions with more natural movement
        nodes.forEach(node => {
          // Add pulsing effect
          node.pulsePhase += 0.02;

          // Move toward original position (center force)
          const dx = node.baseX - node.x;
          const dy = node.baseY - node.y;
          node.vx += dx * config.centerForce;
          node.vy += dy * config.centerForce;

          // Mouse interaction
          if (mouse.active) {
            const mouseDx = node.x - mouse.x;
            const mouseDy = node.y - mouse.y;
            const mouseDistance = Math.sqrt(mouseDx * mouseDx + mouseDy * mouseDy);

            if (mouseDistance < 200) {
              const force = (1 - mouseDistance / 200) * config.mouseForce;
              node.vx += (mouseDx / mouseDistance) * force;
              node.vy += (mouseDy / mouseDistance) * force;
            }
          }

          // Apply velocity
          node.x += node.vx;
          node.y += node.vy;

          // Apply friction
          node.vx *= 0.98;
          node.vy *= 0.98;

          // Soft bounds
          const margin = 50;
          if (node.x < margin) node.vx += 0.1;
          if (node.x > width - margin) node.vx -= 0.1;
          if (node.y < margin) node.vy += 0.1;
          if (node.y > height - margin) node.vy -= 0.1;
        });

        // Draw connections with gradient based on distance
        for (let i = 0; i < nodes.length; i++) {
          for (let j = i + 1; j < nodes.length; j++) {
            const nodeA = nodes[i];
            const nodeB = nodes[j];

            const dx = nodeA.x - nodeB.x;
            const dy = nodeA.y - nodeB.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < config.maxDistance) {
              // 90% visible connections
              const opacity = Math.pow(1 - distance / config.maxDistance, 1.2) * 0.9;

              ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`;
              ctx.lineWidth = 1.0;

              ctx.beginPath();
              ctx.moveTo(nodeA.x, nodeA.y);
              ctx.lineTo(nodeB.x, nodeB.y);
              ctx.stroke();
            }
          }
        }

        // Draw nodes with subtle white glow for background effect
        nodes.forEach(node => {
          // Create radial gradient for subtle glow effect
          const gradient = ctx.createRadialGradient(
            node.x, node.y, 0,
            node.x, node.y, node.radius * 3
          );

          // 90% visible nodes
          gradient.addColorStop(0, `rgba(255, 255, 255, ${node.opacity * 0.9})`);
          gradient.addColorStop(0.4, `rgba(255, 255, 255, ${node.opacity * 0.6})`);
          gradient.addColorStop(1, `rgba(255, 255, 255, 0)`);

          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.radius * 3, 0, Math.PI * 2);
          ctx.fill();

          // 90% visible node cores
          ctx.fillStyle = `rgba(255, 255, 255, ${node.opacity * 0.9})`;
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
          ctx.fill();
        });
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', resizeCanvas);
      if (canvas) {
        canvas.removeEventListener('mousemove', handleMouseMove);
        canvas.removeEventListener('mouseleave', handleMouseLeave);
      }
      cancelAnimationFrame(animationRef.current);
    };
  }, [config, handleMouseMove, handleMouseLeave]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none z-0"
      style={{ background: 'black' }}
    />
  );
};

export const FloatingConstellation = () => {
  // Create deterministic but visually pleasing dots
  const dots: Dot[] = Array.from({ length: 12 }, (_, i) => {
    const angle = (i / 12) * Math.PI * 2;
    const radius = 30 + (i % 3) * 25;

    return {
      id: i,
      delay: (Math.sin(i * 1.3) + 1) * 1.5,
      duration: 4 + ((Math.cos(i * 0.7) + 1) * 2),
      size: 1 + (Math.sin(i) * 0.5),
      x: `${50 + Math.cos(angle) * radius}%`,
      y: `${50 + Math.sin(angle) * radius}%`,
    };
  });

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {dots.map((dot) => (
        <motion.div
          key={dot.id}
          className="absolute w-2 h-2 bg-white/90 rounded-full shadow-lg shadow-white/50"
          initial={{
            scale: 0,
            opacity: 0,
            x: dot.x,
            y: dot.y,
          }}
          animate={{
            scale: [1, 1.4, 1],
            opacity: [0.3, 0.8, 0.3],
            x: [
              dot.x,
              `${50 + (Math.sin(dot.id * 0.7) * 20)}%`,
              dot.x
            ],
            y: [
              dot.y,
              `${50 + (Math.cos(dot.id * 0.9) * 20)}%`,
              dot.y
            ],
          }}
          transition={{
            duration: dot.duration,
            repeat: Infinity,
            delay: dot.delay,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );
};

// Combined component that uses both effects
export const ConstellationBackground = () => {
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
      <NetworkConstellation />
      <FloatingConstellation />
    </div>
  );
};